import * as React from 'react';
import { Link } from 'react-router-dom';
import { DetailsList, DetailsListLayoutMode, IDetailsRowProps, DetailsRow } from '@fluentui/react/lib/DetailsList';
import '../styles/common.css'; // Import CSS styles
import 'react-date-range/dist/styles.css'; // Import the default styles
import 'react-date-range/dist/theme/default.css'; // Import the default theme styles
import 'font-awesome/css/font-awesome.min.css';
import { Metrics, MetricNames } from '../utils/constants';
import AzDevOpsIcon from '../assets/images/Azure-DevOps.svg';


// Define columns for DetailsList
const _columns =(service) => [
    {
        key: 'prTitle',
        name: 'PR Title',
        fieldName: 'prTitle',
        minWidth: 250,
        isResizable: true,
        onRender: (item) => {
            return (
                <div className="listview-cell-truncate" title={item.title}>
                    <Link to={`/service/${service.name}/CommitDetails/${item.commitId}`}>{item.title}</Link>
                </div>
            );
        }
    },
    {
        key: 'authorName',
        name: 'Author',
        fieldName: 'authorName',
        minWidth: 120,
        maxWidth: 180,
        isResizable: true,
        onRender: (item) => {
            return <div className="listview-cell-truncate" title={item.authorName}>{item.authorName}</div>;
        }
    },
    {
        key: 'isRegression',
        name: 'Regression',
        fieldName: 'isRegression',
        minWidth: 150,
        maxWidth: 220,
        isResizable: true,
        onRender: (item) => {
            if (item.dataSources === null && item.regressionMask === 0) {
                return (<span className="listview-no-data">No Data</span>);
            } else {
                const regressions = Metrics.filter(v => (item.regressionMask & Math.pow(2, v)) !== 0).map(m => MetricNames[m]).join('/');
                if (regressions) {
                    return <span className="listview-regression" title={regressions}>{regressions}</span>;
                } else {
                    return <div className="listview-cell-truncate" title={regressions}>{regressions}</div>;
                }
            }
        }
    },
    {
        key: 'prMergeDate',
        name: 'PR Merge Date',
        fieldName: 'prMergeDate',
        minWidth: 140,
        maxWidth: 160,
        isResizable: true,
        onRender: (item) => {
            const mergeDate = new Date(item.prMergeDate);
            const formattedDate = mergeDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
            const formattedTime = mergeDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
            return <div className="listview-date">{formattedDate}<br/><span className="listview-time">{formattedTime}</span></div>;
        }
    },
    {
        key: 'deplStatus',
        name: 'Deployment',
        fieldName: 'deploymentStatusAsString',
        minWidth: 100,
        maxWidth: 120,
        isResizable: true,
        onRender: (item) => {
            return <div className="listview-cell-truncate" title={item.deploymentStatusAsString}>{item.deploymentStatusAsString}</div>;
        }
    },
    {
        key: 'flighting',
        name: 'Flighting',
        fieldName: 'controlStatusAsString',
        minWidth: 100,
        maxWidth: 120,
        isResizable: true,
        onRender: (item) => {
            return <div className="listview-cell-truncate" title={item.controlStatusAsString}>{item.controlStatusAsString}</div>;
        }
    },
    {
        key: 'showDetails',
        name: 'Link',
        fieldName: 'showDetails',
        minWidth: 60,
        maxWidth: 60,
        isResizable: false,
        onRender: (item) => {
            return (
                <a
                    href={`https://${service.azDOOrg}.visualstudio.com/${item.projectName}/_git/${item.repositoryName}/commit/${item.commitId.split('-').pop()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="listview-icon-link"
                >
                    <img src={AzDevOpsIcon} width="20" height="20" alt="Link to commit in AzDevops " />
                </a>
            );
        }
    }
];

export function ListView({ service, commits, auditorView }) {
    return (
        <div className="modern-listview-container">
            <DetailsList
                compact={false}
                items={commits.map(item => ({
                    ...item,
                    isManualRegression: item.isManualRegression !== null ? item.isManualRegression : 'not flagged',
                    auditor: item.auditor !== null ? item.auditor : 'not flagged',
                }))}
                columns={_columns(service)
                    .filter(column => { return auditorView || column.key !== 'authorName' })}
                onRenderRow={_onRenderRow}
                setKey="set"
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={0}
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                checkButtonAriaLabel="select row"
                styles={{
                    root: {
                        marginTop: '0',
                        width: '100%',
                        overflow: 'hidden',
                        '.ms-DetailsList': {
                            overflow: 'hidden',
                        },
                        '.ms-DetailsHeader': {
                            paddingTop: 0,
                            borderBottom: '2px solid var(--color-border)',
                        },
                        '.ms-DetailsHeader-cell': {
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: 'var(--color-text-primary)',
                            backgroundColor: 'var(--color-surface)',
                            padding: 'var(--spacing-md) var(--spacing-sm)',
                            height: '52px',
                            borderRight: '1px solid var(--color-border-light)',
                        },
                        '.ms-DetailsHeader-cell:last-child': {
                            borderRight: 'none',
                        },
                        '.ms-DetailsHeader-cellTitle': {
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: 'var(--color-text-primary)',
                        }
                    }
                }}
            />
        </div>
    );

    function _onRenderRow(props: IDetailsRowProps | undefined): JSX.Element {
        if (!props || !props.item) {
            return null;
        }

        let isManualRegressionClass = '';
        let backgroundColor = '';
        let hoverBackgroundColor = '';

        // Determine row styling - Microsoft Fluent colors
        if (props.item.isManualRegression === true) {
            isManualRegressionClass = 'isManualRegressionTrue';
            backgroundColor = 'var(--color-error-light)';
            hoverBackgroundColor = '#FFD6D8';
        } else if (props.item.isManualRegression === false) {
            isManualRegressionClass = 'isManualRegressionFalse';
            backgroundColor = 'var(--color-success-light)';
            hoverBackgroundColor = '#C7E0C7';
        } else {
            isManualRegressionClass = props.itemIndex % 2 === 0 ? 'evenRow' : 'oddRow';
            backgroundColor = props.itemIndex % 2 === 0 ? 'var(--color-white)' : 'var(--color-surface)';
            hoverBackgroundColor = '#DEECF9';
        }

        return (
            <DetailsRow
                {...props}
                className={isManualRegressionClass}
                styles={{
                    root: {
                        backgroundColor: backgroundColor,
                        borderBottom: '1px solid var(--color-border-light)',
                        minHeight: '52px',
                        transition: 'background-color var(--transition-fast), box-shadow var(--transition-fast)',
                        selectors: {
                            ':hover': {
                                backgroundColor: hoverBackgroundColor,
                                boxShadow: 'var(--shadow-sm)',
                            },
                            '.ms-DetailsRow-cell': {
                                padding: 'var(--spacing-sm)',
                                fontSize: 'var(--font-size-sm)',
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                                minWidth: 0,
                            },
                            '.ms-DetailsRow-cell a': {
                                color: 'var(--color-primary)',
                                fontWeight: 'var(--font-weight-medium)',
                                textDecoration: 'none',
                                transition: 'color var(--transition-fast)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                            '.ms-DetailsRow-cell a:hover': {
                                color: 'var(--color-primary-hover)',
                                textDecoration: 'underline',
                            }
                        }
                    }
                }}
            />
        );
    };

}

